import { Box, Button, Snackbar, Typography } from '@mui/material';
import { AppAccess } from '../types/dashboardSelector';

export type AppAccessClipboardNotification = {
  key: number;
  appCount: number;
  appAccessType: AppAccess['type'];
  submoduleName: string;
  action: 'copied' | 'pasted';
};

type AppAccessPasteSnackbarProps = {
  notification: AppAccessClipboardNotification | null;
  autoHideDuration?: number;
  onUndo: () => void;
  onClose: () => void;
};

const getAccessDisplay = (appAccessType: AppAccess['type'], appCount: number): string => {
  if (appAccessType === 'DEMO') return 'Demo access';
  if (appAccessType === 'FULL_ACCESS') return 'Full access';
  return `${appCount} apps`;
};

const AppAccessPasteSnackbar = ({
  notification,
  autoHideDuration = 4000,
  onUndo,
  onClose,
}: AppAccessPasteSnackbarProps) => {
  if (!notification) {
    return null;
  }

  const { appCount, appAccessType, submoduleName, key, action } = notification;
  const accessDisplay = getAccessDisplay(appAccessType, appCount);

  return (
    <Snackbar
      key={key}
      open
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      autoHideDuration={autoHideDuration}
      onClose={(_, reason) => {
        if (reason === 'clickaway') return;
        onClose();
      }}
      sx={{ zIndex: 15000 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: '16px',
          py: '12px',
          borderRadius: '8px',
          backgroundColor: '#F0F0F1',
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
        }}
      >
        <Typography
          sx={{ fontSize: '14px', fontWeight: 500, color: '#162936', lineHeight: '20px' }}
        >
          {action === 'copied' && (
            <>
              {accessDisplay} App access copied to
              <Box component="span" sx={{ fontWeight: 700, px: '4px' }}>
                {submoduleName}
              </Box>
              Module
            </>
          )}
          {action === 'pasted' && (
            <>
              App access updated to {accessDisplay} for
              <Box component="span" sx={{ fontWeight: 700, px: '4px' }}>
                {submoduleName}
              </Box>
              Module
            </>
          )}
        </Typography>
        {action === 'pasted' && (
          <Button
            variant="text"
            color="primary"
            sx={{
              fontSize: '14px',
              fontWeight: 700,
              color: '#162936',
              lineHeight: '20px',
              textTransform: 'none',
              p: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
            onClick={onUndo}
          >
            Undo
          </Button>
        )}
      </Box>
    </Snackbar>
  );
};

export default AppAccessPasteSnackbar;
